import React from 'react';
import { Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import {
  SuccessContainer,
  IllustrationWrapper,
  Illustration,
  StoreBadgeContainer,
  StoreBadgeLink,
  QRCodeWrapper,
  QRCode,
} from './ReadyToStart.styles';

import AppStoreBadge from '@/assets/AppStore.svg';
import GooglePlayBadge from '@/assets/GooglePlay.svg';
import DaylightIllustration from '@/assets/Party_Confetti.svg';
import {
  DAYLIGHT_APP_STORE_URL,
  DAYLIGHT_GOOGLE_PLAY_URL,
  DAYLIGHT_SMART_LINK_URL,
  SLEEPIO_APP_STORE_URL,
  SLEEPIO_GOOGLE_PLAY_URL,
  SLEEPIO_SMART_LINK_URL,
} from '@/pages/Onboarding/utils/constants';
import { getPlatform } from '@/pages/Onboarding/utils/platformHelpers';
import { Platform, Product } from '@/utils/productHelpers';

type Props = {
  product: Product;
};

const PRODUCT_CONFIG: Record<string, {
  appStore: string;
  googlePlay: string;
  qrCode: string;
  titleKey: string;
  subtitleKey: string;
  desktopSubtitleKey: string;
}> = {
  daylight: {
    appStore: DAYLIGHT_APP_STORE_URL,
    googlePlay: DAYLIGHT_GOOGLE_PLAY_URL,
    qrCode: DAYLIGHT_SMART_LINK_URL,
    titleKey: 'coverage.spring_health.ready_to_start.title',
    subtitleKey: 'coverage.spring_health.ready_to_start.subtitle',
    desktopSubtitleKey: 'coverage.spring_health.ready_to_start.subtitle_desktop',
  },
  sleepio: {
    appStore: SLEEPIO_APP_STORE_URL,
    googlePlay: SLEEPIO_GOOGLE_PLAY_URL,
    qrCode: SLEEPIO_SMART_LINK_URL,
    titleKey: 'coverage.spring_health.ready_to_start.title_sleepio',
    subtitleKey: 'coverage.spring_health.ready_to_start.subtitle_sleepio',
    desktopSubtitleKey: 'coverage.spring_health.ready_to_start.subtitle_sleepio_desktop',
  },
};

const ReadyToStart: React.FC<Props> = ({ product }) => {
  const { t } = useTranslation();
  const platform = getPlatform();

  const config = PRODUCT_CONFIG[product] ?? PRODUCT_CONFIG.daylight;

  const subtitleKey = platform === Platform.Web
    ? config.desktopSubtitleKey
    : config.subtitleKey;

  let storeContent;

  if (platform === Platform.iOS) {
    storeContent = (
      <StoreBadgeContainer>
        <StoreBadgeLink
          href={config.appStore}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="app-store-link"
        >
          <img src={AppStoreBadge} alt="Download on the App Store" />
        </StoreBadgeLink>
      </StoreBadgeContainer>
    );
  } else if (platform === Platform.Android) {
    storeContent = (
      <StoreBadgeContainer>
        <StoreBadgeLink
          href={config.googlePlay}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="google-play-link"
        >
          <img src={GooglePlayBadge} alt="Get it on Google Play" />
        </StoreBadgeLink>
      </StoreBadgeContainer>
    );
  } else {
    storeContent = (
      <QRCodeWrapper>
        <QRCode
          data-testid="qr-code"
          value={config.qrCode}
        />
      </QRCodeWrapper>
    );
  }

  return (
    <SuccessContainer>
      <IllustrationWrapper>
        <Illustration src={DaylightIllustration} alt="Success" />
      </IllustrationWrapper>
      <Typography variant="h2" style={{ marginBottom: 8 }}>
        {t(config.titleKey)}
      </Typography>
      <Typography style={{ marginBottom: 16 }}>
        {t(subtitleKey)}
      </Typography>
      {storeContent}
    </SuccessContainer>
  );
};

export default ReadyToStart;
